import { writeSignupSession, type LinkProofHandoff } from './signupSession';
import type { SignupStep } from './steps';

/**
 * How another auth screen hands a person into the signup route.
 *
 * Login reaches two of these: a provider that would not vouch for the address falls back to an
 * emailed code, and a provider whose address belongs to an existing account that never proved
 * its mailbox has to prove it before the identity may attach. Both are one navigate call, so no
 * screen has to know how the signup route stores its state.
 */

export interface SignupRouteSearch {
	step?: SignupStep;
	email?: string;
	companyID?: string;
	/** Renders the one-line "we could not confirm that email with your provider" explanation. */
	unverified?: boolean;
}

/**
 * The provider refused to vouch for the address, so we ask for it and send a code instead.
 * Usage: `navigate({ to: '/signup', search: unverifiedEmailFallback({ email }) })`.
 */
export const unverifiedEmailFallback = ({ email }: { email: string }): SignupRouteSearch => ({
	step: 'email',
	email,
	unverified: true,
});

/**
 * Park the live provider token and open the proof-before-link screen. The token is kept so the
 * exact same exchange can be repeated the moment the mailbox is proven; it never becomes a
 * session on its own.
 * Usage: `navigate({ to: '/signup', search: beginLinkProof({ handoff }) })`.
 */
export const beginLinkProof = ({ handoff }: { handoff: LinkProofHandoff }): SignupRouteSearch => {
	writeSignupSession({ linkProof: handoff, linkProofCodeSentFor: null, resendAvailableAt: null });
	return { step: 'link-proof', email: handoff.email };
};

/** The social tap created the User; it is signed in and owes us its details. */
export const socialDetailsStep = (): SignupRouteSearch => ({ step: 'social-details' });
